import { useQueryClient, useMutation } from '@tanstack/react-query'
import { postRequestExpertMentor } from '../apis/api-assignation'
import { useZoomContext } from '../context/ZoomContext'
import { useUI } from '../context/UIContext'
import { getSessionParameters } from '../helpers/urls'
import { extractRoomFromTopic } from '../helpers/formatters'
import { useTranslation } from 'react-i18next'
import Axios from 'axios'
import { ToastEscalatedHelpRequest } from '../components/Toast/ToastEscalatedHelpRequest'
import { postRequestTutor } from '../apis/api-widget'
import { notifyToMyTeam } from '../helpers/commandChannel'
import { useUserCourses } from '../pages/meeting-view/hooks/useCourses'

export const useHRRequestHelp = ({ onComplete }: { onComplete: () => void }) => {
    const { t } = useTranslation(['meeting', 'notifications'])
    const { currentCourse } = useUserCourses()
    const queryClient = useQueryClient()
    const { commandChannel } = useZoomContext()
    const { showToast, showError } = useUI()
    const { topic } = getSessionParameters()
    const teamNumber = extractRoomFromTopic(topic) || 0
    const helpRequestType = currentCourse?.courseFlags.helpRequestType

    const { mutate: requestHelp, ...rest } = useMutation({
        mutationFn: async () => {
            if (helpRequestType === 'mentor') return await postRequestExpertMentor()
            if (helpRequestType === 'tutor') return await postRequestTutor(teamNumber)
            return {}
        },

        onSuccess: async () => {
            const message = {
                type: 'help-request',
            }
            await notifyToMyTeam({ message, commandChannel })

            // Then update UI and close modal
            queryClient.invalidateQueries({ queryKey: ['Help Request'] })
            showToast(<ToastEscalatedHelpRequest />, {
                id: 'mentor-escalated',
            })
            onComplete()
        },

        onError: error => {
            // Catch 404 errors
            if (Axios.isAxiosError(error) && error?.response?.status === 404) {
                showToast(
                    t('error.allMentorsBusy', 'All mentors are busy, try again later.', {
                        ns: 'notifications',
                    })
                )
                return
            } else {
                showError(
                    t('error.requestHelp', 'Unavailable to request help at this time.', {
                        ns: 'notifications',
                    })
                )
                console.error(error)
            }
        },
    })

    return { requestHelp, ...rest }
}
